import React, { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";
import "./InstallPrompt.css";

const DISMISSED_BASE = "tallypot-install-dismissed";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function getDeviceType() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua);
  if (isIOS && isSafari) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export function InstallPrompt({ userId }) {
  const [visible, setVisible] = useState(false);
  const [deviceType, setDeviceType] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Key the dismissal per-account so signing in on a NEW device (or a new
  // account on this device) surfaces the install instructions once.
  const dismissedKey = userId ? `${DISMISSED_BASE}:${userId}` : DISMISSED_BASE;

  useEffect(() => {
    if (!userId) return; // only prompt once the user is signed in
    if (isStandalone()) return;
    if (localStorage.getItem(dismissedKey)) return;

    const type = getDeviceType();
    setDeviceType(type);

    if (type === "ios") {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(t);
    }

    let t;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      t = setTimeout(() => setVisible(true), 3000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(t);
    };
  }, [userId, dismissedKey]);

  function dismiss() {
    localStorage.setItem(dismissedKey, "1");
    setVisible(false);
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferredPrompt(null);
  }

  if (!visible) return null;

  return (
    <div className="ip-overlay" onClick={dismiss}>
          <div
            className="ip-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="ip-close" onClick={dismiss} aria-label="Dismiss">
              <X size={16} />
            </button>

            <img src="/logo192.png" alt="TallyPot" className="ip-app-icon" />

            <h3 className="ip-title">Add TallyPot to your home screen</h3>
            <p className="ip-subtitle">Full app experience — no browser bar, works offline</p>

            {deviceType === "ios" ? (
              <div className="ip-steps">
                <div className="ip-step">
                  <div className="ip-step-icon"><Share size={18} /></div>
                  <span>Tap the <strong>Share</strong> button <span className="ip-share-arrow">↑</span> at the bottom of Safari</span>
                </div>
                <div className="ip-step-divider" />
                <div className="ip-step">
                  <div className="ip-step-icon"><Plus size={18} /></div>
                  <span>Scroll down and tap <strong>Add to Home Screen</strong></span>
                </div>
              </div>
            ) : (
              <button className="ip-install-btn" onClick={handleInstall}>
                Install App
              </button>
            )}
          </div>
    </div>
  );
}
